/**
 * A markdown body's headings, and the anchor each one answers to.
 *
 * ONE computation, run by both sides: `anchorHeadings` walks a tree in the
 * order it is read and gives each heading its id. The view runs it over the
 * tree it is about to draw and the outline runs it over the same body's tree,
 * so a contents entry and the heading it names hold the same id because the
 * same function handed it out — never because two functions happened to
 * agree. A contents entry that scrolls somewhere else looks like a page that
 * did not move, and nothing reports it.
 */

import type { FootnoteDefinition, Heading } from 'mdast'
import { type OutlineEntry, PAGE_SCOPE } from './format'
import {
  type MarkdownNode,
  type MarkdownTree,
  nodesOf,
  parseMarkdown,
  wordsOf,
} from './markdown-tree'

/**
 * The heading levels that take an anchor and enter an outline.
 *
 * Deeper headings are structure inside a part, not a way in.
 */
const ANCHORED_LEVELS: readonly number[] = [1, 2, 3]

/**
 * What stands between a scope and a slug.
 *
 * A slug holds letters, digits and hyphens and never this, so a scoped id
 * cannot be read two ways, and cannot be an id somebody wrote by hand.
 */
const SCOPE_MARK = '.'

/**
 * Hands out an id per heading, unique within one document.
 *
 * An id is derived from the heading's OWN words, so it says what it reaches.
 * A repeated name takes a suffix, and the suffix is checked against what has
 * been handed out rather than counted: counting alone collides with a heading
 * genuinely called "Beta 2" sitting below two called "Beta", and two elements
 * answering to one id means the second is unreachable.
 *
 * **An id is per DOCUMENT, and a page may show several.** A chat is many turns
 * on one page and two of them may carry the same heading, so each document is
 * given a `scope` and its ids are made inside it.
 *
 * Where a name's count has reached is remembered per name, so a document that
 * repeats one heading ten thousand times is numbered in one pass. Counting
 * from one for each of them is the square of that.
 */
export function createSlugger(scope: string): (text: string) => string {
  const used = new Set<string>()
  const reached = new Map<string, number>()
  return (text: string) => {
    const base = `${scope}${SCOPE_MARK}${headingSlug(text)}`
    let n = reached.get(base) ?? 1
    let id = n === 1 ? base : `${base}-${n}`
    while (used.has(id)) {
      n += 1
      id = `${base}-${n}`
    }
    used.add(id)
    reached.set(base, n + 1)
    return id
  }
}

/**
 * The id a fragment of an address names, on the one document a page is about.
 *
 * An address may name a heading by its slug alone, as a person would write it
 * and as a link kept from another time has it. The heading answers to the
 * slug inside the page's scope, so that is where a bare fragment leads; one
 * that already names the scope is taken as it stands.
 */
export function pageAnchorOf(fragment: string): string {
  const lead = `${PAGE_SCOPE}${SCOPE_MARK}`
  return fragment.startsWith(lead) ? fragment : `${lead}${fragment}`
}

/** A stable slug for a heading, derived from the words a reader sees. */
export function headingSlug(text: string): string {
  const base = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '')
  /* A heading of nothing but punctuation still needs an id to be reached by. */
  return base || 'section'
}

export type MarkdownHeading = {
  id: string
  label: string
  /** One of the anchored levels. */
  level: number
}

/**
 * What every id a document's footnotes are given begins with.
 *
 * A heading's id is the scope, the mark and a slug, and a slug never holds the
 * mark — so an id holding it twice is one no heading can be given, whatever
 * the heading says. A heading called "FN 1" and the first footnote would
 * otherwise answer to one id, and the note's marker would scroll to the
 * heading.
 */
export function noteIdPrefix(scope: string): string {
  return `${scope}${SCOPE_MARK}note${SCOPE_MARK}`
}

/**
 * Every heading of a tree the page draws, in the order it draws them.
 *
 * The body first, then the footnotes: a note is drawn at the foot of the page,
 * only where something refers to it, once whatever the count of references,
 * in the order of the first reference to each — references inside a note
 * drawn earlier included — and the first definition of a name is the one
 * drawn. A heading inside a note nothing refers to is not on the page, so it
 * is neither anchored nor listed.
 */
function* drawnHeadings(tree: MarkdownTree): Generator<Heading> {
  const notes = new Map<string, FootnoteDefinition>()
  for (const { node } of nodesOf(tree)) {
    if (node.type !== 'footnoteDefinition') continue
    const name = node.identifier.toUpperCase()
    if (!notes.has(name)) notes.set(name, node)
  }

  const referred: string[] = []
  const seen = new Set<string>()
  function* walk(root: MarkdownNode): Generator<Heading> {
    const note = (n: MarkdownNode) =>
      n !== root && n.type === 'footnoteDefinition'
    for (const { node } of nodesOf(root, note)) {
      if (note(node)) continue
      if (node.type === 'heading') yield node
      if (node.type === 'footnoteReference') {
        const name = node.identifier.toUpperCase()
        if (!seen.has(name)) referred.push(name)
        seen.add(name)
      }
    }
  }

  yield* walk(tree)
  /* The list grows while it is read: a note may refer to another. */
  for (let i = 0; i < referred.length; i++) {
    const note = notes.get(referred[i])
    if (note) yield* walk(note)
  }
}

/**
 * Gives every anchored heading the page draws its id, and says which it gave,
 * in the order the page draws them.
 *
 * Every heading drawn is counted, wherever it sits — under a quote, inside a
 * list item, written with an underline instead of hashes, inside a footnote —
 * because an id skipped here is a suffix the page hands to the wrong heading.
 *
 * The id is left on the node as the attribute a renderer draws, so the tree a
 * view is handed back is already anchored and the view computes nothing.
 */
export function anchorHeadings(
  tree: MarkdownTree,
  scope: string,
): MarkdownHeading[] {
  const out: MarkdownHeading[] = []
  const idFor = createSlugger(scope)

  for (const node of drawnHeadings(tree)) {
    if (!ANCHORED_LEVELS.includes(node.depth)) continue
    const label = wordsOf(node).replace(/\s+/g, ' ').trim()
    const id = idFor(label)
    /* `hProperties` is where this family of libraries reads the attributes of
       the element a node becomes. Assigned rather than declared: the type
       belongs to the renderer's side, which this package does not name. */
    Object.assign(node, {
      data: { ...node.data, hProperties: { ...hPropertiesOf(node.data), id } },
    })
    out.push({ id, label, level: node.depth })
  }
  return out
}

/** The attributes a node already carries for its element, if any. */
function hPropertiesOf(data: unknown): Record<string, unknown> {
  if (typeof data !== 'object' || data === null) return {}
  if (!('hProperties' in data)) return {}
  const held = data.hProperties
  return typeof held === 'object' && held !== null ? { ...held } : {}
}

/**
 * A document's headings as a contents nests them.
 *
 * A heading with no words is anchored and not listed: it has an id to keep the
 * count true, and no name for a contents to show.
 *
 * **The top tier is the shallowest level the document actually uses**, not `#`.
 * Docs here are commonly written with `##` as their top heading and no `#` at
 * all; keyed on the absolute level, every one of those nests under whichever
 * heading came first and the contents lists a single entry for the whole
 * document.
 *
 * Depth is kept by a STACK rather than by "deeper than the top", so a third
 * tier sits under the second: comparing against the top alone puts an `###` and
 * the `##` above it in the same list of children, which reads as two flat tiers
 * however many the author wrote.
 */
export function markdownOutline(body: string): OutlineEntry[] {
  const out: OutlineEntry[] = []
  /* The entry each level is currently collecting into, deepest last. */
  const open: { level: number; entry: OutlineEntry }[] = []

  for (const h of anchorHeadings(parseMarkdown(body), PAGE_SCOPE)) {
    if (!h.label) continue
    const entry: OutlineEntry = { id: h.id, label: h.label }
    while (open.length && open[open.length - 1].level >= h.level) open.pop()

    const parent = open[open.length - 1]?.entry
    if (parent) parent.children = [...(parent.children ?? []), entry]
    else out.push(entry)

    open.push({ level: h.level, entry })
  }
  return out
}
